/**
 * The four "which one?" fields of the CRM forms: owner, person, organization
 * and deal. Each is core's picker with this module's search plugged in —
 * `SubjectPicker` for the owner, who is a user, and `RecordPicker` for the rest.
 *
 * A form holds a picked record as `CrmRefDto` (`{ id, name }`), the shape the
 * server answers with, so an edit form starts from the DTO without a lookup.
 */
import { useTranslation } from 'react-i18next';
import type { CrmRefDto, SubjectDto } from '@forsys/shared';
import { RecordPicker } from '../../components/RecordPicker';
import { SubjectPicker } from '../../components/SubjectPicker';
import { useRecordSearch } from '../../hooks/useRecordSearch';
import { useSubjectSearch } from '../../hooks/useSubjectSearch';
import { listDeals, listOrganizations, listPersons, searchOwners } from './api';

/** A picked record, with an optional second line for the list. */
interface Option extends CrmRefDto {
  caption?: string | null;
}

/** How many a picker offers. Enough to choose from; a longer list is a search. */
const OFFERED = 20;

interface PickerProps {
  value: CrmRefDto | null;
  onChange: (value: CrmRefDto | null) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

function toRef(option: Option | null): CrmRefDto | null {
  return option ? { id: option.id, name: option.name } : null;
}

function usePicker(search: (q: string) => Promise<readonly Option[]>) {
  const { t } = useTranslation('crm');
  const found = useRecordSearch(search);
  return {
    options: found.options,
    loading: found.loading,
    onQueryChange: found.setQuery,
    getKey: (option: Option) => option.id,
    getLabel: (option: Option) => option.name,
    getCaption: (option: Option) => option.caption,
    noOptionsText: t('picker.noMatches'),
  };
}

export function PersonPicker({ value, onChange, label, helperText, disabled }: PickerProps) {
  const { t } = useTranslation('crm');
  const picker = usePicker(async (q) =>
    (await listPersons({ ...(q ? { q } : {}), limit: OFFERED })).persons.map((person) => ({
      id: person.id,
      name: person.name,
      caption: person.organization?.name ?? person.email,
    })),
  );
  return (
    <RecordPicker<Option>
      {...picker}
      value={value}
      onChange={(option) => onChange(toRef(option))}
      label={label ?? t('field.person')}
      {...(helperText ? { helperText } : {})}
      {...(disabled ? { disabled } : {})}
    />
  );
}

export function OrganizationPicker({ value, onChange, label, helperText, disabled }: PickerProps) {
  const { t } = useTranslation('crm');
  const picker = usePicker(async (q) =>
    (await listOrganizations({ ...(q ? { q } : {}), limit: OFFERED })).organizations.map((organization) => ({
      id: organization.id,
      name: organization.name,
      caption: organization.address,
    })),
  );
  return (
    <RecordPicker<Option>
      {...picker}
      value={value}
      onChange={(option) => onChange(toRef(option))}
      label={label ?? t('field.organization')}
      {...(helperText ? { helperText } : {})}
      {...(disabled ? { disabled } : {})}
    />
  );
}

/** Open deals only: an activity is planned on something still being worked. */
export function DealPicker({ value, onChange, label, helperText, disabled }: PickerProps) {
  const { t } = useTranslation('crm');
  const picker = usePicker(async (q) =>
    (await listDeals({ ...(q ? { q } : {}), status: 'open', limit: OFFERED })).deals.map((deal) => ({
      id: deal.id,
      name: deal.title,
      caption: deal.organization?.name ?? deal.person?.name ?? null,
    })),
  );
  return (
    <RecordPicker<Option>
      {...picker}
      value={value}
      onChange={(option) => onChange(toRef(option))}
      label={label ?? t('field.deal')}
      {...(helperText ? { helperText } : {})}
      {...(disabled ? { disabled } : {})}
    />
  );
}

/** The owner is a user, so this is core's subject picker over `/api/crm/owners`. */
export function OwnerPicker({ value, onChange, label, helperText, disabled }: PickerProps) {
  const { t } = useTranslation('crm');
  const search = useSubjectSearch(searchOwners);
  const subject: SubjectDto | null = value ? { type: 'user', id: value.id, displayName: value.name } : null;

  return (
    <SubjectPicker
      options={search.options}
      value={subject}
      loading={search.loading}
      label={label ?? t('field.owner')}
      placeholder={t('picker.searchOwner')}
      {...(helperText ? { helperText } : {})}
      {...(disabled ? { disabled } : {})}
      onChange={(picked) => onChange(picked ? { id: picked.id, name: picked.displayName } : null)}
      onQueryChange={search.setQuery}
    />
  );
}
